import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import SeoHead from '@/components/SeoHead';
import { publishedResourcePages } from '@/data/resourcePages';

const legacyResourcePages = [
  {
    title: 'How to Tailor a Resume to a Job Description',
    path: '/how-to-tailor-a-resume-to-a-job-description',
    excerpt: 'A practical guide to customizing your resume around a target role without sounding generic.'
  },
  {
    title: 'Why Your Resume Is Not Getting Interviews',
    path: '/why-your-resume-is-not-getting-interviews',
    excerpt: 'Common reasons resumes get overlooked and how to improve what hiring teams and systems actually see.'
  }
];

function ResourceCard({ title, excerpt, path, badge }) {
  return (
    <Link
      to={path}
      className="group flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{badge}</p>
          <h2 className="mt-3 font-heading text-2xl font-semibold leading-tight text-foreground">
            {title}
          </h2>
        </div>
        <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>
      <p className="mt-4 text-sm leading-7 text-muted-foreground">{excerpt}</p>
    </Link>
  );
}

export default function ResourcesIndexPage() {
  const staticResourcePages = useMemo(
    () => [...publishedResourcePages].sort((left, right) => left.title.localeCompare(right.title)),
    []
  );

  return (
    <div className="min-h-screen bg-surface">
      <SeoHead
        title="Resources | JOBFITAI"
        description="Browse JOBFITAI resume, ATS, tailoring, and application resources with stable public resource pages and practical job search guidance."
        canonicalPath="/resources"
        robots="index,follow"
      />
      <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <div className="mb-8 flex items-center gap-3">
          <MainMenu />
          <Link to="/" className="text-primary transition-colors hover:text-primary-hover">
            Back to Home
          </Link>
        </div>

        <section className="rounded-[2rem] border border-border bg-white px-8 py-10 shadow-soft md:px-10 md:py-12">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Resources</p>
          <h1 className="mt-4 font-heading text-4xl font-bold leading-tight text-foreground md:text-5xl">
            Resume guides and resource pages
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
            Browse the JOBFITAI resource library. Featured guides and resource pages stay available here with stable URLs for easier access.
          </p>
        </section>

        <section className="mt-10">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-foreground">Featured Guides</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Existing hardcoded resources remain live alongside the static resource library.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {legacyResourcePages.map((page) => (
              <ResourceCard
                key={page.path}
                title={page.title}
                excerpt={page.excerpt}
                path={page.path}
                badge="Guide"
              />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-foreground">Resource Library</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Static resource pages available at their existing public URLs.
              </p>
            </div>
            <span className="rounded-full bg-white px-4 py-2 text-sm font-medium text-muted-foreground shadow-soft">
              {staticResourcePages.length} published
            </span>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {staticResourcePages.map((page) => (
              <ResourceCard
                key={page.slug}
                title={page.title}
                excerpt={page.excerpt || page.meta_description || 'Published resource page'}
                path={`/resources/${page.slug}`}
                badge="Resource Page"
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
